/*
  Board view of the Tasks page: one lane per group (Section by default; Person, Project or Due date with
  "Group by", idea 5). Wave 1 behaviour is kept for the Section lanes: quick "Add task" field per lane,
  drag & drop between and within lanes, done-this-week cards with "Show done", edge fades + arrows when the
  lanes do not fit. In the other groupings the cards keep their order by due date and "Add task" opens the
  task pane with that person / project / day filled in.
  Wave 3: with `dragToPeople` (desktop) cards drag in every grouping — onto a person in Team load (idea 10);
  only the Section grouping also takes them onto other lanes and cards.
  `readOnly` (BUG-002: Tasks is View only for the role, or a Viewer account): no lane "+", no quick "Add task" field
  or button, no drag and no drop — the cards only open the task (TaskCard gets readOnly through cardProps).
  Also exports the phone list (lanes as a list of cards grouped by due date) and the loading skeleton.
*/
import { forwardRef, useCallback, useEffect, useRef, useState } from 'react';
import {
  CalendarDays,
  CalendarRange,
  CalendarX2,
  Check,
  ChevronLeft,
  ChevronRight,
  CircleDashed,
  CircleDot,
  Loader2,
  OctagonAlert,
  Plus,
  TriangleAlert,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import TaskCard from './TaskCard';
import { isCancelled } from './taskModel';

/** With "Show done": this week's done cards, then cancelled ones — each under its own small heading. */
function ClosedGroups({ tasks, name, cardProps }) {
  if (!tasks.length) return null;
  const groups = [
    { key: 'done', label: 'Done this week', items: tasks.filter((task) => !isCancelled(task)) },
    { key: 'cancelled', label: 'Cancelled', items: tasks.filter(isCancelled) },
  ].filter((group) => group.items.length);
  return groups.map((group) => (
    <div key={group.key} className="flex flex-col gap-2" data-testid={`task-${group.key}-group-${name}`}>
      <span className="px-2 pt-1 text-[11px] font-bold uppercase tracking-[0.05em] text-ink-3">
        {group.label} · {group.items.length}
      </span>
      {group.items.map((task) => (
        <TaskCard key={task.id} task={task} done draggable={false} selected={cardProps.selectedTaskId === task.id} {...cardProps} />
      ))}
    </div>
  ));
}

const QuickCreate = forwardRef(function QuickCreate({ section, onCreate }, ref) {
  const [draft, setDraft] = useState('');
  const [creating, setCreating] = useState(false);

  const submit = async (event) => {
    event.preventDefault();
    const name = draft.trim();
    if (!name || creating) return;
    setCreating(true);
    try {
      await onCreate(section, name);
      setDraft('');
    } catch {
      // the board already showed the error; keep the typed name so nothing is lost
    } finally {
      setCreating(false);
    }
  };

  return (
    <form onSubmit={submit}>
      <label
        className={cn(
          'flex h-8 cursor-text items-center gap-1.5 rounded-[10px] border border-dashed border-[color:var(--line-dashed)] px-2.5 text-ink-2 transition-colors',
          'hover:bg-surface focus-within:border-solid focus-within:border-[color:var(--brand-soft-border)] focus-within:bg-surface focus-within:shadow-focus'
        )}
      >
        {creating ? <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin" aria-hidden="true" /> : <Plus className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />}
        <input
          ref={ref}
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Escape') {
              setDraft('');
              event.currentTarget.blur();
            }
          }}
          disabled={creating}
          dir="auto"
          placeholder="Add task"
          aria-label={`New task in ${section}`}
          data-testid={`quick-create-${section}`}
          className="h-full min-w-0 flex-1 bg-transparent text-start text-[13px] font-semibold text-ink outline-none placeholder:text-ink-2 focus:placeholder:font-medium focus:placeholder:text-ink-4"
        />
        {draft.trim() ? <kbd className="ui-kbd h-[18px] px-1 text-[10.5px]">Enter</kbd> : null}
      </label>
    </form>
  );
});

export function StatusLegend() {
  const items = [
    { Icon: CircleDashed, label: 'Open', className: 'text-ink-3' },
    { Icon: CircleDot, label: 'In progress', className: 'text-status-blue' },
    { Icon: OctagonAlert, label: 'Blocked', className: 'text-status-red' },
  ];
  return (
    <span className="hidden items-center gap-2.5 text-[12px] font-semibold text-ink-2 lg:inline-flex" aria-hidden="true">
      {items.map(({ Icon, label, className }) => (
        <span key={label} className="inline-flex items-center gap-1">
          <Icon className={cn('h-[13px] w-[13px]', className)} strokeWidth={2.25} />
          {label}
        </span>
      ))}
    </span>
  );
}

// Phones: lanes stack. Wider screens: up to 5 lanes per row (at least 222px each); more lanes wrap onto the next row, so the board never scrolls sideways.
const LANES_GRID = 'grid items-start gap-3 grid-cols-1 sm:grid-cols-[repeat(auto-fill,minmax(max(222px,calc((100%-48px)/5)),1fr))]';

function BoardScroller({ laneCount, children, onDragEnd }) {
  const scrollerRef = useRef(null);
  const [edges, setEdges] = useState({ left: false, right: false });

  const update = useCallback(() => {
    const element = scrollerRef.current;
    if (!element) return;
    const left = element.scrollLeft > 4;
    const right = element.scrollLeft + element.clientWidth < element.scrollWidth - 4;
    setEdges((current) => (current.left === left && current.right === right ? current : { left, right }));
  }, []);

  useEffect(() => {
    update();
    const element = scrollerRef.current;
    if (!element || typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(update);
    observer.observe(element);
    if (element.firstElementChild) observer.observe(element.firstElementChild);
    return () => observer.disconnect();
  }, [laneCount, update]);

  const scrollByLanes = (direction) => {
    const element = scrollerRef.current;
    const lane = element?.firstElementChild?.firstElementChild;
    if (!element) return;
    const step = lane ? lane.getBoundingClientRect().width + 12 : 272;
    element.scrollBy({ left: direction * step, behavior: 'smooth' });
  };

  const arrow = 'absolute top-1.5 z-[3] grid h-8 w-8 place-items-center rounded-full border border-line bg-surface text-ink-2 shadow-raised transition-colors hover:bg-surface-hover hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring';
  return (
    <div className="relative" data-testid="tasks-board-lanes">
      <div ref={scrollerRef} onScroll={update} className="pb-2">
        <div className={LANES_GRID} onDragEnd={onDragEnd}>{children}</div>
      </div>
      {edges.left ? (
        <>
          <span className="pointer-events-none absolute inset-y-0 left-0 z-[2] hidden w-10 bg-gradient-to-r from-[color:var(--app-background)] to-transparent sm:block" aria-hidden="true" />
          <button type="button" className={cn(arrow, '-left-3 hidden sm:grid')} onClick={() => scrollByLanes(-1)} aria-label="Scroll to earlier lanes">
            <ChevronLeft className="h-4 w-4" aria-hidden="true" />
          </button>
        </>
      ) : null}
      {edges.right ? (
        <>
          <span className="pointer-events-none absolute inset-y-0 right-0 z-[2] hidden w-14 bg-gradient-to-l from-[color:var(--app-background)] to-transparent sm:block" aria-hidden="true" />
          <button type="button" className={cn(arrow, '-right-3 hidden sm:grid')} onClick={() => scrollByLanes(1)} aria-label="Scroll to more lanes" data-testid="tasks-board-scroll-right">
            <ChevronRight className="h-4 w-4" aria-hidden="true" />
          </button>
        </>
      ) : null}
    </div>
  );
}

export function BoardSkeleton() {
  return (
    <div className={LANES_GRID} aria-busy="true" data-testid="tasks-board-loading">
      <span className="sr-only">Loading tasks…</span>
      {[3, 2, 3].map((count, lane) => (
        <div key={lane} className="flex flex-col gap-2 rounded-[14px] bg-[color:var(--line-2)] p-1.5" aria-hidden="true">
          <div className="flex h-[30px] items-center gap-2 px-2">
            <span className="h-3 w-24 animate-pulse rounded bg-[color:var(--line)]" />
            <span className="h-3 w-5 animate-pulse rounded-full bg-[color:var(--line)]" />
          </div>
          {Array.from({ length: count }).map((_, index) => (
            <div key={index} className="flex flex-col gap-2 rounded-xl border border-line bg-surface px-3 py-3 shadow-card">
              <span className="h-3.5 w-4/5 animate-pulse rounded bg-[color:var(--line-2)]" />
              <span className="h-3 w-1/2 animate-pulse rounded bg-[color:var(--line-2)]" />
              <span className="flex items-center justify-between">
                <span className="h-4 w-24 animate-pulse rounded bg-[color:var(--line-2)]" />
                <span className="h-[22px] w-[22px] animate-pulse rounded-full bg-[color:var(--line-2)]" />
              </span>
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

const laneId = (key) => `task-lane-${String(key).replace(/[^a-z0-9]+/gi, '-').toLowerCase()}`;
const TONE_DOT = { red: 'tone-red', amber: 'tone-amber', gray: 'tone-gray' };

function Lane({
  lane,
  sectionMode,
  readOnly = false,
  dragToPeople,
  dragOver,
  dropBeforeId,
  draggingId,
  onCreate,
  onAdd,
  onLaneDragOver,
  onLaneDrop,
  cardProps,
}) {
  const { tasks, doneTasks } = lane;
  const quickCreateRef = useRef(null);
  const name = sectionMode ? lane.section : lane.label;
  const canAdd = !readOnly && (sectionMode || Boolean(lane.prefill));
  const dropTarget = sectionMode && !readOnly;
  const addHere = () => (sectionMode ? quickCreateRef.current?.focus() : onAdd(lane.prefill));
  return (
    <section
      data-testid={`task-column-${name}`}
      aria-labelledby={laneId(lane.key)}
      onDragOver={dropTarget ? (event) => onLaneDragOver(event, lane.section) : undefined}
      onDrop={dropTarget ? (event) => onLaneDrop(event, lane.section) : undefined}
      className={cn(
        'flex min-w-0 snap-start flex-col gap-2 rounded-[14px] bg-[color:var(--line-2)] p-1.5 transition-shadow',
        dragOver && 'shadow-[inset_0_0_0_2px_var(--brand-soft-border)]'
      )}
    >
      <header className="flex h-[30px] items-center gap-2 pl-2">
        {lane.tone && TONE_DOT[lane.tone] ? <span className={cn('ui-tone-dot', TONE_DOT[lane.tone])} aria-hidden="true" /> : null}
        <h2 id={laneId(lane.key)} className="min-w-0 truncate text-[13px] font-bold text-ink">{name}</h2>
        <span className="rounded-full bg-surface px-[7px] py-px text-[11px] font-bold tabular-nums text-ink-2">
          {lane.key === 'closed'
            ? <>{doneTasks.length}<span className="sr-only"> done or cancelled</span></>
            : <>{tasks.length}<span className="sr-only"> pending</span></>}
        </span>
        <span className="flex-1" />
        {canAdd ? (
          <Button
            type="button"
            variant="ghost"
            size="icon-xs"
            className="text-ink-3"
            aria-label={`Add task to ${name}`}
            title={`Add task to ${name}`}
            onClick={addHere}
          >
            <Plus aria-hidden="true" />
          </Button>
        ) : null}
      </header>

      {tasks.map((task) => (
        <div key={task.id} className="relative">
          {dropBeforeId === task.id ? (
            <span className="pointer-events-none absolute -top-[5px] left-1 right-1 h-[3px] rounded-full bg-brand" aria-hidden="true" />
          ) : null}
          <TaskCard task={task} dragging={draggingId === task.id} draggable={!readOnly && (sectionMode || dragToPeople)} selected={cardProps.selectedTaskId === task.id} {...cardProps} />
        </div>
      ))}
      {dragOver && !dropBeforeId && draggingId ? (
        <span className="pointer-events-none mx-1 h-[3px] rounded-full bg-brand" aria-hidden="true" />
      ) : null}
      <ClosedGroups tasks={doneTasks} name={name} cardProps={cardProps} />
      {readOnly ? null : sectionMode ? (
        <QuickCreate ref={quickCreateRef} section={lane.section} onCreate={onCreate} />
      ) : lane.prefill ? (
        <button
          type="button"
          onClick={() => onAdd(lane.prefill)}
          className="flex h-8 items-center gap-1.5 rounded-[10px] border border-dashed border-[color:var(--line-dashed)] px-2.5 text-[13px] font-semibold text-ink-2 transition-colors hover:bg-surface focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <Plus className="h-3.5 w-3.5" aria-hidden="true" /> Add task
        </button>
      ) : null}
    </section>
  );
}

/**
 * The lanes. `drag` = { taskId, section, beforeId } and the drag handlers come from the page (Section
 * grouping only).
 */
export function TaskLanes({
  lanes,
  sectionMode,
  readOnly = false,
  dragToPeople = false,
  drag,
  onCreate,
  onAdd,
  onLaneDragOver,
  onLaneDrop,
  onDragEnd,
  cardProps,
}) {
  return (
    <BoardScroller laneCount={lanes.length} onDragEnd={onDragEnd}>
      {lanes.map((lane) => (
        <Lane
          key={lane.key}
          lane={lane}
          sectionMode={sectionMode}
          readOnly={readOnly}
          dragToPeople={dragToPeople}
          dragOver={sectionMode && Boolean(drag.taskId) && drag.section === lane.section}
          dropBeforeId={sectionMode && drag.section === lane.section ? drag.beforeId : null}
          draggingId={drag.taskId}
          onCreate={onCreate}
          onAdd={onAdd}
          onLaneDragOver={onLaneDragOver}
          onLaneDrop={onLaneDrop}
          cardProps={cardProps}
        />
      ))}
    </BoardScroller>
  );
}

const DUE_ICONS = { overdue: TriangleAlert, today: CalendarDays, blocked: OctagonAlert, later: CalendarRange, none: CalendarX2, closed: Check };
const DUE_TEXT = { red: 'text-status-red-ink', amber: 'text-status-amber-ink', gray: 'text-ink-3' };

/** Phones: the groups as a list of full-width cards (default: Overdue / Today / Blocked / Later / No date). */
export function TaskList({ lanes, cardProps, total, doneCount, showDone, onToggleDone }) {
  return (
    <div className="flex flex-col gap-4" data-testid="tasks-list">
      {lanes.map((lane) => {
        const Icon = DUE_ICONS[lane.key];
        return (
          <section key={lane.key} aria-labelledby={laneId(`list-${lane.key}`)} className="flex flex-col gap-2" data-testid={`task-column-${lane.section || lane.label}`}>
            <h2 id={laneId(`list-${lane.key}`)} className={cn('flex items-center gap-1.5 text-[11.5px] font-bold uppercase tracking-[0.06em]', DUE_TEXT[lane.tone] || 'text-ink-3')}>
              {Icon ? <Icon className="h-[13px] w-[13px]" aria-hidden="true" /> : null}
              {lane.section || lane.label} · {lane.key === 'closed' ? lane.doneTasks.length : lane.tasks.length}
            </h2>
            {lane.tasks.map((task) => <TaskCard key={task.id} task={task} draggable={false} selected={cardProps.selectedTaskId === task.id} {...cardProps} />)}
            {lane.key === 'closed'
              ? lane.doneTasks.map((task) => <TaskCard key={task.id} task={task} done draggable={false} selected={cardProps.selectedTaskId === task.id} {...cardProps} />)
              : <ClosedGroups tasks={lane.doneTasks} name={lane.section || lane.label} cardProps={cardProps} />}
          </section>
        );
      })}
      <p className="flex flex-wrap items-center justify-center gap-1 py-2 text-[13px] text-ink-3">
        <Check className="h-3.5 w-3.5" aria-hidden="true" />
        That’s all {total} · {doneCount} done this week ·
        <button type="button" onClick={onToggleDone} className="font-semibold text-brand-ink hover:underline">
          {showDone ? 'Hide done' : 'Show done'}
        </button>
      </p>
    </div>
  );
}
